import { FlowButton } from 'components/ui/flow-button';
import { useEffect, useMemo, useRef, useState } from 'react';
import TmsConnectionBoundary from 'components/TmsConnectionBoundary';
import { useLocation, useNavigate } from 'react-router-dom';
import { CalendarDays, CheckCircle2, CreditCard, ExternalLink, Loader2 } from 'lucide-react';
import {
  buildConsultationCalendarUrl,
  captureConsultationPayPalOrder,
  confirmConsultationPaymentStatus,
  createConsultationPayPalOrder,
  createConsultationStripeCheckoutSession,
  formatConsultationMoney,
  getConsultationOfferConfig,
  loadConsultationPaymentConfig,
  startConsultationSwiftPayment,
} from 'lib/consultationGateway';
import { reportClientError } from 'lib/clientErrorReporter';
import { toast } from 'sonner';

const DEFAULT_FLOW_STATE = {
  paymentStatus: 'not_started',
  bookingStatus: 'not_started',
  bookingId: '',
  orderId: '',
  captureId: '',
  paymentProvider: '',
  checkoutSessionId: '',
  bankTransferReference: '',
};

const FLOW_SESSION_TTL_MS = 30 * 60 * 1000;
const BASE_ALLOWED_PAYPAL_HOSTS = new Set([
  'paypal.com',
  'www.paypal.com',
  'www.sandbox.paypal.com',
  'sandbox.paypal.com',
  'api.paypal.com',
]);
const BASE_ALLOWED_CALENDAR_HOSTS = new Set([
  'cal.com',
  'www.cal.com',
  'app.cal.com',
]);
const BASE_ALLOWED_STRIPE_HOSTS = new Set([
  'checkout.stripe.com',
  'billing.stripe.com',
]);
const RETURN_PAYMENT_PROVIDERS = new Set(['paypal', 'stripe']);
const CONFIGURED_REDIRECT_HOSTS = new Set(
  String(process.env.REACT_APP_TRUSTED_REDIRECT_HOSTS || '')
    .split(',')
    .map((host) => sanitizeText(host).toLowerCase())
    .filter(Boolean)
);

function sanitizeText(value) {
  return String(value || '').trim();
}

function sanitizeReturnPaymentProvider(value) {
  const provider = sanitizeText(value).toLowerCase();
  return RETURN_PAYMENT_PROVIDERS.has(provider) ? provider : '';
}

function getStartedPaymentProvider(paymentProvider) {
  const provider = sanitizeText(paymentProvider).toLowerCase();
  return RETURN_PAYMENT_PROVIDERS.has(provider) ? provider : '';
}

function isAllowedRedirectHost(hostname, fallbackHosts = [], domainType = 'default') {
  const normalized = sanitizeText(hostname).toLowerCase();
  if (!normalized) {
    return false;
  }

  const allowedHosts = new Set(Array.from(CONFIGURED_REDIRECT_HOSTS));
  if (normalized === 'localhost' || normalized === '127.0.0.1' || normalized === '::1') {
    allowedHosts.add(normalized);
  }

  if (typeof window !== 'undefined') {
    allowedHosts.add(window.location.hostname.toLowerCase());
  }

  for (const host of fallbackHosts) {
    const fallback = sanitizeText(host).toLowerCase();
    if (fallback) {
      allowedHosts.add(fallback);
    }
  }

  if (domainType === 'paypal') {
    BASE_ALLOWED_PAYPAL_HOSTS.forEach((host) => allowedHosts.add(host));
  }

  if (domainType === 'calendar') {
    BASE_ALLOWED_CALENDAR_HOSTS.forEach((host) => allowedHosts.add(host));
  }

  if (domainType === 'stripe') {
    BASE_ALLOWED_STRIPE_HOSTS.forEach((host) => allowedHosts.add(host));
  }

  return allowedHosts.has(normalized);
}

function parseSafeUrl(rawUrl) {
  try {
    const parsed = new URL(sanitizeText(rawUrl));
    const protocol = parsed.protocol.toLowerCase();
    const isLocalhost = ['localhost', '127.0.0.1', '::1'].includes(
      sanitizeText(parsed.hostname).toLowerCase()
    );

    if (!['https:', 'http:'].includes(protocol)) {
      return null;
    }

    if (protocol === 'http:' && !isLocalhost) {
      return null;
    }

    return parsed;
  } catch (error) {
    return null;
  }
}

function getRedirectUrl(rawUrl, domainType = 'default', fallbackHosts = []) {
  const parsed = parseSafeUrl(rawUrl);
  if (!parsed || !isAllowedRedirectHost(parsed.hostname, fallbackHosts, domainType)) {
    return '';
  }
  return parsed.toString();
}

function sanitizeFlowStatus(value, fallback = 'not_started') {
  const normalized = sanitizeText(value);
  if (normalized === 'completed' || normalized === 'started') {
    return normalized;
  }
  return fallback;
}

function sanitizeBookingStatus(value, fallback = 'not_started') {
  const normalized = sanitizeText(value);
  if (normalized === 'opened' || normalized === 'completed') {
    return normalized;
  }
  return fallback;
}

function sanitizePersistedFlowState(raw) {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const expiresAt = Number(raw.expiresAt);
  if (Number.isFinite(expiresAt) && expiresAt < Date.now()) {
    return null;
  }

  return {
    paymentStatus: sanitizeFlowStatus(raw.paymentStatus, 'not_started'),
    bookingStatus: sanitizeBookingStatus(raw.bookingStatus, 'not_started'),
    bookingId: sanitizeText(raw.bookingId),
    orderId: sanitizeText(raw.orderId),
    captureId: sanitizeText(raw.captureId),
    paymentProvider: sanitizeText(raw.paymentProvider),
    checkoutSessionId: sanitizeText(raw.checkoutSessionId),
    bankTransferReference: sanitizeText(raw.bankTransferReference),
  };
}

function safeReadSession(key) {
  if (!key || typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    return sanitizePersistedFlowState(parsed);
  } catch (error) {
    return null;
  }
}

function safeWriteSession(key, value) {
  if (!key || typeof window === 'undefined') {
    return;
  }

  try {
    if (!value || typeof value !== 'object') {
      return;
    }

    const payload = {
      paymentStatus: sanitizeFlowStatus(value.paymentStatus, 'not_started'),
      bookingStatus: sanitizeBookingStatus(value.bookingStatus, 'not_started'),
      bookingId: sanitizeText(value.bookingId).slice(0, 128),
      orderId: sanitizeText(value.orderId).slice(0, 128),
      captureId: sanitizeText(value.captureId).slice(0, 128),
      paymentProvider: sanitizeText(value.paymentProvider).slice(0, 40),
      checkoutSessionId: sanitizeText(value.checkoutSessionId).slice(0, 160),
      bankTransferReference: sanitizeText(value.bankTransferReference).slice(0, 160),
      expiresAt: Date.now() + FLOW_SESSION_TTL_MS,
    };
    window.sessionStorage.setItem(key, JSON.stringify(payload));
  } catch (error) {
    console.error('Failed to persist consultation state:', error);
  }
}

function getApprovalUrl(data) {
  if (!data || typeof data !== 'object') {
    return '';
  }

  const directCandidates = [
    data.approvalUrl,
    data.approveUrl,
    data.redirectUrl,
    data.redirect_url,
    data.approval_url,
    data.links?.find?.((link) => link?.rel === 'approve')?.href,
    data.links?.find?.((link) => link?.rel === 'payer-action')?.href,
    data.paypal?.approvalUrl,
  ];

  return directCandidates.find((value) => sanitizeText(value)) || '';
}

function getCheckoutUrl(data) {
  if (!data || typeof data !== 'object') {
    return '';
  }

  return [
    data.checkoutUrl,
    data.url,
    data.redirectUrl,
    data.redirect_url,
    data.session?.url,
  ].find((value) => sanitizeText(value)) || '';
}

function getOrderReference(data) {
  if (!data || typeof data !== 'object') {
    return '';
  }

  return [
    data.orderId,
    data.orderID,
    data.id,
    data.paypalOrderId,
    data.order?.id,
  ].find((value) => sanitizeText(value)) || '';
}

function getBookingReference(data) {
  if (!data || typeof data !== 'object') {
    return '';
  }

  return [
    data.bookingId,
    data.consultationBookingId,
    data.booking?.id,
  ].find((value) => sanitizeText(value)) || '';
}

function getCheckoutSessionReference(data) {
  if (!data || typeof data !== 'object') {
    return '';
  }

  return [
    data.checkoutSessionId,
    data.sessionId,
    data.session?.id,
  ].find((value) => sanitizeText(value)) || '';
}

function getCaptureReference(data) {
  if (!data || typeof data !== 'object') {
    return '';
  }

  return [
    data.captureId,
    data.id,
    data.paypalCaptureId,
    data.capture?.id,
    data.payment?.captureId,
  ].find((value) => sanitizeText(value)) || '';
}

function buildCleanPageUrl(pathname, result, provider = 'paypal') {
  if (typeof window === 'undefined') {
    return pathname;
  }

  const url = new URL(window.location.origin + pathname);
  url.searchParams.set('cf_checkout', provider);
  url.searchParams.set('cf_result', result);
  return url.toString();
}

function getPaymentProviderLabel(provider) {
  switch (sanitizeText(provider).toLowerCase()) {
    case 'stripe':
      return 'Stripe';
    case 'swift':
      return 'SWIFT';
    case 'paypal':
      return 'PayPal';
    default:
      return 'payment';
  }
}

export default function ConsultationNextSteps(props) {
  return <TmsConnectionBoundary><ConnectedConsultationNextSteps {...props} /></TmsConnectionBoundary>;
}

function ConnectedConsultationNextSteps({
  sourcePage,
  storageKey,
  leadContext,
  introTitle = 'Continue into consultation booking',
  introBody = 'If you want to secure a fit review immediately, continue with payment and then open the calendar.',
  completionBody = 'We received your information and will respond within the next 24 hours.',
  singleStepFlow = false,
  autoScrollOnMount = true,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [flowState, setFlowState] = useState(() => safeReadSession(storageKey) || DEFAULT_FLOW_STATE);
  const [activeStep, setActiveStep] = useState('intro');
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [isCapturingOrder, setIsCapturingOrder] = useState(false);
  const [isLoadingPaymentConfig, setIsLoadingPaymentConfig] = useState(true);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [paymentConfigError, setPaymentConfigError] = useState('');
  const [swiftInstructions, setSwiftInstructions] = useState(null);
  const panelRef = useRef(null);
  const fallbackOffer = useMemo(() => getConsultationOfferConfig(), []);
  const offer = useMemo(() => {
    if (!paymentConfig) {
      return {
        ...fallbackOffer,
        paymentProvider: fallbackOffer.paymentProvider || 'paypal',
        providerReady: false,
        providerLabel: getPaymentProviderLabel(fallbackOffer.paymentProvider || 'paypal'),
        ownerActionRequired: false,
      };
    }

    const paymentProvider = paymentConfig.paymentProvider || 'manual';
    const providerLabel = paymentConfig.provider?.displayName || getPaymentProviderLabel(paymentProvider);
    return {
      ...fallbackOffer,
      amount: paymentConfig.amount ?? paymentConfig.paymentAmount ?? fallbackOffer.amount,
      currency: paymentConfig.currency || paymentConfig.paymentCurrency || fallbackOffer.currency,
      paymentRequired: Boolean(paymentConfig.paymentRequired),
      paymentProvider,
      providerReady: !paymentConfig.paymentRequired || Boolean(paymentConfig.readiness?.ready),
      providerLabel,
      ownerActionRequired: Boolean(paymentConfig.readiness?.ownerActionRequired),
      unavailableReason: paymentConfig.readiness?.unavailableReason || paymentConfig.provider?.unavailableReason || '',
    };
  }, [fallbackOffer, paymentConfig]);

  const leadReference = sanitizeText(
    leadContext?.referenceId
    || leadContext?.leadId
    || leadContext?.id
    || leadContext?.submissionId
  );

  const calendarUrl = useMemo(() => {
    const built = buildConsultationCalendarUrl({
      name: leadContext?.contactName,
      email: leadContext?.email,
      company: leadContext?.companyName,
      notes: [
        sourcePage ? `Source page: ${sourcePage}` : '',
        leadReference ? `Submission reference: ${leadReference}` : '',
      ].filter(Boolean).join(' | '),
      query: {
        leadReference,
        sourcePage,
      },
    });

    return built;
  }, [leadContext, leadReference, sourcePage]);

  const trustedCalendarUrl = useMemo(() => {
    return getRedirectUrl(calendarUrl, 'calendar');
  }, [calendarUrl]);

  useEffect(() => {
    let cancelled = false;
    const loadConfig = async () => {
      setIsLoadingPaymentConfig(true);
      setPaymentConfigError('');
      try {
        const config = await loadConsultationPaymentConfig();
        if (!cancelled) {
          setPaymentConfig(config);
        }
      } catch (error) {
        console.error('Failed to load consultation payment config:', error);
        reportClientError('consultation_payment_config_failed', error, { sourcePage, leadReference });
        if (!cancelled) {
          setPaymentConfig(null);
          setPaymentConfigError(error?.message || 'Payment settings could not be loaded.');
        }
      } finally {
        if (!cancelled) {
          setIsLoadingPaymentConfig(false);
        }
      }
    };

    loadConfig();
    return () => {
      cancelled = true;
    };
  }, [leadReference, sourcePage]);

  useEffect(() => {
    safeWriteSession(storageKey, {
      ...DEFAULT_FLOW_STATE,
      ...flowState,
    });
  }, [flowState, storageKey]);

  useEffect(() => {
    if (!autoScrollOnMount || typeof window === 'undefined' || !panelRef.current) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 120);

    return () => window.clearTimeout(timeoutId);
  }, [autoScrollOnMount]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const checkout = sanitizeReturnPaymentProvider(params.get('cf_checkout'));
    const result = params.get('cf_result');
    const token = sanitizeText(params.get('token'));
    const payerId = sanitizeText(params.get('PayerID') || params.get('payerId'));
    const checkoutSessionId = sanitizeText(params.get('session_id'));

    if (!checkout) {
      return;
    }

    if (result === 'cancelled') {
      toast.error('Payment was cancelled. You can restart it at any time.');
      navigate(location.pathname, { replace: true });
      return;
    }

    if (checkout === 'paypal' && (!token || result !== 'approved' || isCapturingOrder || flowState.paymentStatus === 'completed')) {
      return;
    }

    if (checkout === 'stripe' && (!checkoutSessionId || result !== 'approved' || isCapturingOrder || flowState.paymentStatus === 'completed')) {
      return;
    }

    const startedProvider = getStartedPaymentProvider(flowState.paymentProvider);
    if (startedProvider && startedProvider !== checkout) {
      reportClientError('payment_return_provider_mismatch', new Error('Payment return provider does not match the stored checkout session.'), {
        sourcePage,
        leadReference,
        expectedProvider: startedProvider,
        returnedProvider: checkout,
      });
      toast.error('This payment return does not match the checkout session. Please restart payment.');
      navigate(location.pathname, { replace: true });
      return;
    }

    if (checkout === 'paypal' && flowState.orderId && flowState.orderId !== token) {
      reportClientError('payment_return_reference_mismatch', new Error('PayPal return token does not match the stored checkout session.'), {
        sourcePage,
        leadReference,
      });
      toast.error('This payment return does not match the checkout session. Please restart payment.');
      navigate(location.pathname, { replace: true });
      return;
    }

    if (checkout === 'stripe' && flowState.checkoutSessionId && flowState.checkoutSessionId !== checkoutSessionId) {
      reportClientError('payment_return_reference_mismatch', new Error('Stripe return session does not match the stored checkout session.'), {
        sourcePage,
        leadReference,
      });
      toast.error('This payment return does not match the checkout session. Please restart payment.');
      navigate(location.pathname, { replace: true });
      return;
    }

    const runCapture = async () => {
      let shouldClearCheckoutParams = false;
      setIsCapturingOrder(true);
      try {
        const bookingId = sanitizeText(flowState.bookingId);
        if (checkout === 'paypal' && !bookingId) {
          throw new Error('Payment session is missing the booking reference. Please restart checkout.');
        }

        if (checkout === 'stripe') {
          const statusResult = await confirmConsultationPaymentStatus({
            bookingId,
            checkoutSessionId,
          });

          shouldClearCheckoutParams = true;
          if (!statusResult.readyForCalendar) {
            setFlowState((current) => ({
              ...current,
              paymentProvider: 'stripe',
              bookingId: bookingId || statusResult.bookingId,
              checkoutSessionId,
              paymentStatus: 'started',
            }));
            toast.info('Stripe payment is processing. Refresh this page in a moment to confirm it.');
            return;
          }

          setFlowState((current) => ({
            ...current,
            paymentProvider: 'stripe',
            paymentStatus: 'completed',
            bookingId: bookingId || statusResult.bookingId,
            orderId: current.orderId || checkoutSessionId,
            checkoutSessionId,
            captureId: statusResult.checkoutSessionId || checkoutSessionId,
          }));
          toast.success('Payment confirmed. You can open the calendar now.');
          return;
        }

        const captureResult = await captureConsultationPayPalOrder({
          bookingId,
          orderId: token,
          token,
          payerId,
          leadReference,
          sourcePage,
          companyName: leadContext?.companyName,
          contactName: leadContext?.contactName,
          email: leadContext?.email,
          phone: leadContext?.phone,
          address: leadContext?.companyAddress,
          companyAddress: leadContext?.companyAddress,
          companyZipCode: leadContext?.companyZipCode,
          dotNumber: leadContext?.dotNumber,
          mcNumber: leadContext?.mcNumber,
          bookingPaymentMode: 'carrierflow_paypal',
        }, { paymentConfig });

        shouldClearCheckoutParams = true;
        setFlowState((current) => ({
          ...current,
          paymentProvider: 'paypal',
          paymentStatus: 'completed',
          bookingId,
          orderId: current.orderId || token,
          captureId: getCaptureReference(captureResult),
        }));
        toast.success('Payment confirmed. You can open the calendar now.');
      } catch (error) {
        console.error('Payment confirmation failed:', error);
        reportClientError('payment_confirmation_failed', error, { sourcePage, leadReference, checkout });
        toast.error('Payment approval was received, but confirmation failed. Keep this page open and refresh to retry confirmation.');
      } finally {
        setIsCapturingOrder(false);
        if (shouldClearCheckoutParams) {
          navigate(location.pathname, { replace: true });
        }
      }
    };

    runCapture();
  }, [flowState.bookingId, flowState.checkoutSessionId, flowState.orderId, flowState.paymentProvider, flowState.paymentStatus, isCapturingOrder, leadContext, leadReference, location.pathname, location.search, navigate, paymentConfig, sourcePage]);

  const paymentComplete = flowState.paymentStatus === 'completed';
  const paymentStarted = flowState.paymentStatus === 'started';
  const paymentSettingsReady = Boolean(paymentConfig) && !isLoadingPaymentConfig && !paymentConfigError;
  const canOpenCalendar = paymentSettingsReady && (!offer.paymentRequired || paymentComplete);
  const canStartPayment = paymentSettingsReady
    && offer.paymentRequired
    && offer.providerReady
    && !isCreatingOrder
    && !isCapturingOrder;

  useEffect(() => {
    if (!singleStepFlow) {
      return;
    }

    if (paymentComplete || (!offer.paymentRequired && paymentSettingsReady)) {
      setActiveStep('calendar');
      return;
    }

    if (paymentStarted) {
      setActiveStep('payment');
    }
  }, [offer.paymentRequired, paymentComplete, paymentSettingsReady, paymentStarted, singleStepFlow]);

  const handleStartPayment = async () => {
    if (!paymentSettingsReady) {
      toast.error('Payment settings are still loading. Please try again in a moment.');
      return;
    }
    if (!offer.providerReady) {
      toast.error('Payment is not configured yet. We will follow up manually.');
      return;
    }

    setIsCreatingOrder(true);
    try {
      const basePayload = {
        leadReference,
        sourcePage,
        companyName: leadContext?.companyName,
        contactName: leadContext?.contactName,
        email: leadContext?.email,
        phone: leadContext?.phone,
        address: leadContext?.companyAddress,
        companyAddress: leadContext?.companyAddress,
        companyZipCode: leadContext?.companyZipCode,
        dotNumber: leadContext?.dotNumber,
        mcNumber: leadContext?.mcNumber,
        amount: offer.amount,
        currency: offer.currency,
        label: offer.label,
        bookingPaymentMode: `carrierflow_${offer.paymentProvider}`,
      };

      if (offer.paymentProvider === 'stripe') {
        const result = await createConsultationStripeCheckoutSession({
          ...basePayload,
          returnUrl: buildCleanPageUrl(location.pathname, 'approved', 'stripe'),
          cancelUrl: buildCleanPageUrl(location.pathname, 'cancelled', 'stripe'),
        }, { paymentConfig });

        const checkoutUrl = getCheckoutUrl(result);
        const trustedCheckoutUrl = getRedirectUrl(checkoutUrl, 'stripe');
        const bookingId = getBookingReference(result);
        const checkoutSessionId = getCheckoutSessionReference(result);

        if (!trustedCheckoutUrl || !bookingId || !checkoutSessionId) {
          const checkoutError = new Error('The payment service did not return a valid Stripe checkout session.');
          reportClientError('stripe_checkout_session_invalid', checkoutError, { sourcePage, leadReference });
          throw checkoutError;
        }

        setFlowState((current) => ({
          ...current,
          paymentProvider: 'stripe',
          bookingId,
          orderId: checkoutSessionId,
          checkoutSessionId,
          paymentStatus: 'started',
        }));

        window.location.href = trustedCheckoutUrl;
        return;
      }

      if (offer.paymentProvider === 'swift') {
        const result = await startConsultationSwiftPayment(basePayload, { paymentConfig });
        const bookingId = getBookingReference(result);
        const reference = sanitizeText(result?.reference || result?.bankTransferReference);

        if (!bookingId || !reference) {
          throw new Error('The payment service did not return SWIFT payment instructions.');
        }

        setSwiftInstructions(result.instructions || null);
        setFlowState((current) => ({
          ...current,
          paymentProvider: 'swift',
          bookingId,
          orderId: reference,
          bankTransferReference: reference,
          paymentStatus: 'started',
        }));
        toast.success('SWIFT payment instructions are ready.');
        return;
      }

      const result = await createConsultationPayPalOrder({
        ...basePayload,
        bookingPaymentMode: 'carrierflow_paypal',
        returnUrl: buildCleanPageUrl(location.pathname, 'approved', 'paypal'),
        cancelUrl: buildCleanPageUrl(location.pathname, 'cancelled', 'paypal'),
      }, { paymentConfig });

      const approvalUrl = getApprovalUrl(result);
      const trustedApprovalUrl = getRedirectUrl(approvalUrl, 'paypal');
      const bookingId = getBookingReference(result);
      const orderId = getOrderReference(result);

      if (!trustedApprovalUrl) {
        const approvalError = new Error('The payment service did not return a PayPal approval link.');
        reportClientError('paypal_approval_url_untrusted', approvalError, { sourcePage, leadReference });
        throw approvalError;
      }

      if (!bookingId || !orderId) {
        const referenceError = new Error('The payment service did not return a booking reference.');
        reportClientError('paypal_booking_reference_missing', referenceError, { sourcePage, leadReference });
        throw referenceError;
      }

      setFlowState((current) => ({
        ...current,
        paymentProvider: 'paypal',
        bookingId,
        orderId,
        paymentStatus: 'started',
      }));

      window.location.href = trustedApprovalUrl;
    } catch (error) {
      console.error('Payment start failed:', error);
      reportClientError('payment_start_failed', error, { sourcePage, leadReference, provider: offer.paymentProvider });
      toast.error(error?.message || 'Unable to start payment.');
    } finally {
      setIsCreatingOrder(false);
    }
  };

  const handleOpenCalendar = () => {
    if (!calendarUrl) {
      toast.error('Calendar link is not configured yet.');
      return;
    }

    if (!trustedCalendarUrl) {
      reportClientError('calendar_url_untrusted', new Error('Calendar URL is not trusted.'), { sourcePage, leadReference });
      toast.error('Calendar URL is not trusted. Please contact support.');
      return;
    }

    setFlowState((current) => ({
      ...current,
      bookingStatus: 'opened',
    }));

    window.open(trustedCalendarUrl, '_blank', 'noopener,noreferrer');
  };

  const handleContinueFromIntro = () => {
    setActiveStep(offer.paymentRequired ? 'payment' : 'calendar');
  };

  if (singleStepFlow) {
    const steps = [
      { id: 'intro', label: 'Info received', complete: true },
      { id: 'payment', label: 'Payment', complete: paymentComplete },
      { id: 'calendar', label: 'Calendar', complete: flowState.bookingStatus === 'opened' },
    ];
    const visibleStep = paymentComplete && activeStep === 'payment' ? 'calendar' : activeStep;

    return (
      <section
        ref={panelRef}
        className="mt-6 relative overflow-hidden border border-gold/25 bg-[#090806] shadow-[0_28px_90px_rgba(0,0,0,0.42)]"
        data-testid={`${sourcePage}-consultation-flow`}
        aria-labelledby={`${sourcePage}-consultation-title`}
      >
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_12%_18%,rgba(76,141,204,0.18),transparent_30%),linear-gradient(135deg,rgba(255,255,255,0.06),transparent_42%)]" />
        <div className="absolute left-0 top-0 h-full w-px bg-gradient-to-b from-transparent via-gold/60 to-transparent" />
        <div className="absolute right-5 top-5 h-5 w-5 border-r border-t border-gold/45" />
        <div className="absolute bottom-5 left-5 h-5 w-5 border-b border-l border-gold/45" />

        <div className="relative z-10 p-6 md:p-8 lg:p-10">
          <div className="mb-7 flex flex-wrap items-center gap-3">
            <span className="inline-flex h-8 items-center border border-gold/25 bg-gold/[0.08] px-3 text-[10px] uppercase tracking-[0.28em] text-accent-ink font-body">
              Next step
            </span>
            <span className="h-px w-12 bg-gold/50" />
            <span className="text-[10px] uppercase tracking-[0.24em] text-copy/35 font-body">
              One step at a time
            </span>
          </div>

          <div className="mb-8 grid grid-cols-3 border border-ink/10 bg-black/20">
            {steps.map((step, index) => {
              const isActive = step.id === visibleStep;
              return (
                <button
                  key={step.id}
                  type="button"
                  onClick={() => {
                    if (step.id === 'intro' || step.id === 'payment' || canOpenCalendar) {
                      setActiveStep(step.id);
                    }
                  }}
                  className={`min-h-[72px] border-r border-ink/10 px-3 py-4 text-left last:border-r-0 transition-colors ${isActive ? 'bg-gold/[0.08]' : 'bg-transparent'} ${step.id === 'calendar' && !canOpenCalendar ? 'cursor-not-allowed opacity-55' : 'hover:bg-ink/[0.035]'}`}
                  aria-current={isActive ? 'step' : undefined}
                  disabled={step.id === 'calendar' && !canOpenCalendar}
                >
                  <span className={`block text-[10px] uppercase tracking-[0.24em] font-body ${isActive || step.complete ? 'text-accent-ink' : 'text-copy/38'}`}>
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className="mt-2 block text-xs font-body text-copy/68">
                    {step.label}
                  </span>
                </button>
              );
            })}
          </div>

          {visibleStep === 'intro' ? (
            <div className="min-h-[360px] flex flex-col justify-center">
              <div className="max-w-3xl">
                <div className="mb-6 flex h-12 w-12 items-center justify-center border border-gold/25 bg-gold/[0.08] text-accent-ink">
                  <CheckCircle2 className="h-6 w-6" strokeWidth={1.5} />
                </div>
                <h3 id={`${sourcePage}-consultation-title`} className="font-display text-4xl md:text-5xl text-ink tracking-tight leading-tight mb-5">
                  {introTitle}
                </h3>
                <p className="font-body text-copy/58 leading-[1.8] max-w-2xl">
                  {introBody}
                </p>
                <p className="mt-5 font-body text-sm text-copy/38 leading-relaxed max-w-2xl">
                  {completionBody}
                </p>
                <FlowButton
                  type="button"
                  onClick={handleContinueFromIntro}
                  className="mt-8 inline-flex items-center gap-3  px-7 py-4 text-[10px] font-body font-bold uppercase tracking-[0.22em]  transition-colors "
                  data-testid={`${sourcePage}-consultation-continue`}
                >
                  Continue
                </FlowButton>
              </div>
            </div>
          ) : null}

          {visibleStep === 'payment' ? (
            <div className="min-h-[360px] grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_260px] gap-8 lg:items-center">
              <div>
                <div className="mb-6 flex h-12 w-12 items-center justify-center border border-gold/25 bg-gold/[0.08] text-accent-ink">
                  <CreditCard className="h-6 w-6" strokeWidth={1.5} />
                </div>
                <p className="mb-4 text-[10px] uppercase tracking-[0.3em] text-accent-ink/78 font-body">
                  Secure the meeting
                </p>
                <h3 id={`${sourcePage}-consultation-title`} className="font-display text-4xl md:text-5xl text-ink tracking-tight leading-tight mb-5">
                  Continue with payment.
                </h3>
                <p className="font-body text-copy/58 leading-[1.8] max-w-2xl">
                  The consultation fee reserves the paid booking path. After confirmation, the calendar opens so you can choose the meeting time.
                </p>

                {offer.paymentRequired ? (
                  paymentComplete ? (
                    <div className="mt-7 border border-gold/20 bg-gold/[0.06] p-5">
                      <p className="font-body text-sm text-copy/78">Payment confirmed.</p>
                      <p className="mt-2 font-body text-xs text-copy/38 leading-relaxed">
                        Reference: {flowState.captureId || flowState.orderId || 'Captured'}
                      </p>
                    </div>
                  ) : (
                    <div className="mt-7">
                      <p className="font-body text-sm text-copy/62 leading-relaxed">
                        {isLoadingPaymentConfig
                          ? 'Loading current payment settings...'
                          : paymentConfigError
                            ? 'Payment settings could not be loaded. We will follow up manually.'
                            : offer.ownerActionRequired
                              ? 'Payment setup needs owner review before checkout can start.'
                              : `Payment method: ${offer.providerLabel}.`}
                      </p>
                      {flowState.paymentProvider === 'swift' && flowState.bankTransferReference ? (
                        <div className="mt-4 space-y-2 border border-ink/10 bg-ink/[0.025] p-4">
                          <p className="font-body text-xs text-copy/58 leading-relaxed">
                            Transfer reference: <span className="text-accent-ink">{flowState.bankTransferReference}</span>
                          </p>
                          {swiftInstructions?.beneficiaryName ? (
                            <p className="font-body text-xs text-copy/38 leading-relaxed">Beneficiary: {swiftInstructions.beneficiaryName}</p>
                          ) : null}
                          {swiftInstructions?.bankName ? (
                            <p className="font-body text-xs text-copy/38 leading-relaxed">Bank: {swiftInstructions.bankName}</p>
                          ) : null}
                          {swiftInstructions?.iban ? (
                            <p className="font-body text-xs text-copy/38 leading-relaxed">IBAN: {swiftInstructions.iban}</p>
                          ) : null}
                          {swiftInstructions?.swiftBic ? (
                            <p className="font-body text-xs text-copy/38 leading-relaxed">SWIFT/BIC: {swiftInstructions.swiftBic}</p>
                          ) : null}
                        </div>
                      ) : null}
                      <FlowButton
                        type="button"
                        onClick={handleStartPayment}
                        disabled={!canStartPayment}
                        className="mt-6 inline-flex w-full max-w-sm items-center justify-center gap-3  px-7 py-4 text-[10px] font-body font-bold uppercase tracking-[0.22em]  transition-colors  disabled:cursor-not-allowed disabled:opacity-50"
                        data-testid={`${sourcePage}-consultation-pay`}
                      >
                        {isCreatingOrder || isCapturingOrder ? (
                          <>
                            Processing <Loader2 className="w-4 h-4 animate-spin" />
                          </>
                        ) : (
                          <>
                            {offer.paymentProvider === 'swift' ? 'Get SWIFT Details' : 'Continue to Pay'}
                          </>
                        )}
                      </FlowButton>
                    </div>
                  )
                ) : (
                  <div className="mt-7 flex items-start gap-3 border border-gold/20 bg-gold/[0.06] p-5">
                    <CheckCircle2 className="w-5 h-5 text-accent-ink mt-0.5 flex-shrink-0" />
                    <p className="font-body text-sm text-copy/70 leading-relaxed">No payment is required before booking.</p>
                  </div>
                )}
              </div>

              <div className="border border-ink/10 bg-ink/[0.035] p-6">
                <p className="text-[10px] uppercase tracking-[0.24em] text-copy/35 font-body mb-3">
                  Discovery call
                </p>
                <p className="font-display text-5xl text-accent-ink leading-none">
                  {formatConsultationMoney(offer.amount, offer.currency)}
                </p>
                <p className="mt-4 text-xs leading-relaxed text-copy/38 font-body">
                  Paid booking opens the calendar after {getPaymentProviderLabel(offer.paymentProvider)} confirmation.
                </p>
              </div>
            </div>
          ) : null}

          {visibleStep === 'calendar' ? (
            <div className="min-h-[360px] flex flex-col justify-center">
              <div className="max-w-3xl">
                <div className="mb-6 flex h-12 w-12 items-center justify-center border border-gold/25 bg-gold/[0.08] text-accent-ink">
                  <CalendarDays className="h-6 w-6" strokeWidth={1.5} />
                </div>
                <p className="mb-4 text-[10px] uppercase tracking-[0.3em] text-accent-ink/78 font-body">
                  Choose date and time
                </p>
                <h3 id={`${sourcePage}-consultation-title`} className="font-display text-4xl md:text-5xl text-ink tracking-tight leading-tight mb-5">
                  Open the calendar.
                </h3>
                <p className="font-body text-copy/58 leading-[1.8] max-w-2xl">
                  {canOpenCalendar ? 'Payment is ready. Choose the slot that fits your team.' : 'Calendar access unlocks after payment is confirmed.'}
                </p>
                <FlowButton
                  type="button"
                  onClick={handleOpenCalendar}
                  disabled={!canOpenCalendar || !calendarUrl}
                  className="mt-8 inline-flex w-full max-w-sm items-center justify-center gap-3 border border-gold/35  px-7 py-4 text-[10px] font-body uppercase tracking-[0.22em]  transition-colors hover:border-gold  disabled:cursor-not-allowed disabled:opacity-40"
                  data-testid={`${sourcePage}-consultation-calendar`}
                >
                  Open Calendar <CalendarDays className="w-4 h-4" />
                </FlowButton>
                {flowState.bookingStatus === 'opened' && trustedCalendarUrl ? (
                  <a
                    href={trustedCalendarUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-5 inline-flex items-center gap-2 text-accent-ink text-[10px] uppercase tracking-[0.2em] font-body"
                    data-testid={`${sourcePage}-consultation-calendar-link`}
                  >
                    Direct booking link <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      </section>
    );
  }

  return (
    <section
      ref={panelRef}
      className="mt-6 relative overflow-hidden border border-gold/25 bg-[#090806] shadow-[0_28px_90px_rgba(0,0,0,0.42)]"
      data-testid={`${sourcePage}-consultation-flow`}
      aria-labelledby={`${sourcePage}-consultation-title`}
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_12%_18%,rgba(76,141,204,0.18),transparent_30%),linear-gradient(135deg,rgba(255,255,255,0.06),transparent_42%)]" />
      <div className="absolute -right-24 -top-28 h-72 w-72 rounded-full border border-gold/10 bg-gold/[0.035] blur-sm" />
      <div className="absolute left-0 top-0 h-full w-px bg-gradient-to-b from-transparent via-gold/60 to-transparent" />
      <div className="absolute right-5 top-5 h-5 w-5 border-r border-t border-gold/45" />
      <div className="absolute bottom-5 left-5 h-5 w-5 border-b border-l border-gold/45" />

      <div className="relative z-10 grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_330px]">
        <div className="p-6 md:p-8 lg:p-10">
          <div className="mb-5 flex flex-wrap items-center gap-3">
            <span className="inline-flex h-8 items-center border border-gold/25 bg-gold/[0.08] px-3 text-[10px] uppercase tracking-[0.28em] text-accent-ink font-body">
              Next step
            </span>
            <span className="h-px w-12 bg-gold/50" />
            <span className="text-[10px] uppercase tracking-[0.24em] text-copy/35 font-body">
              Information received
            </span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_220px] gap-8 lg:items-end">
            <div>
              <h3 id={`${sourcePage}-consultation-title`} className="font-display text-3xl md:text-4xl text-ink tracking-tight leading-tight mb-4">
                {introTitle}
              </h3>
              <p className="font-body text-copy/56 leading-[1.75] max-w-2xl">
                {introBody}
              </p>
            </div>

            <div className="border border-ink/10 bg-ink/[0.035] p-5">
              <p className="text-[10px] uppercase tracking-[0.24em] text-copy/35 font-body mb-2">
                Discovery call
              </p>
              <p className="font-display text-3xl text-accent-ink leading-none">
                {formatConsultationMoney(offer.amount, offer.currency)}
              </p>
              <p className="mt-3 text-xs leading-relaxed text-copy/38 font-body">
                Paid booking opens the calendar after {getPaymentProviderLabel(offer.paymentProvider)} confirmation.
              </p>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="group relative overflow-hidden border border-gold/20 bg-canvas/70 p-5">
              <div className="absolute inset-x-0 top-0 h-px bg-gold/70" />
              <div className="mb-4 flex items-center justify-between gap-4">
                <p className="text-[10px] uppercase tracking-[0.24em] text-accent-ink/85 font-body">01 Stored</p>
                <CheckCircle2 className="h-5 w-5 text-accent-ink" strokeWidth={1.5} />
              </div>
              <p className="font-body text-sm text-copy/78 leading-relaxed">We received your information.</p>
              <p className="mt-3 font-body text-xs text-copy/38 leading-relaxed">{completionBody}</p>
            </div>

            <div className="group relative overflow-hidden border border-ink/10 bg-surface/85 p-5">
              <div className={`absolute inset-x-0 top-0 h-px ${paymentComplete ? 'bg-gold/70' : paymentStarted ? 'bg-ink/45' : 'bg-ink/15'}`} />
              <div className="mb-4 flex items-center justify-between gap-4">
                <p className="text-[10px] uppercase tracking-[0.24em] text-copy/42 font-body">02 Payment</p>
                {paymentComplete ? (
                  <CheckCircle2 className="h-5 w-5 text-accent-ink" strokeWidth={1.5} />
                ) : (
                  <CreditCard className="h-5 w-5 text-copy/38" strokeWidth={1.5} />
                )}
              </div>
              {offer.paymentRequired ? (
                paymentComplete ? (
                  <div>
                    <p className="font-body text-sm text-copy/78">Payment confirmed.</p>
                    <p className="mt-3 font-body text-xs text-copy/38 leading-relaxed">
                      Reference: {flowState.captureId || flowState.orderId || 'Captured'}
                    </p>
                  </div>
                ) : (
                  <div>
                    <p className="font-body text-sm text-copy/62 leading-relaxed">
                      {offer.label} fee: <span className="text-accent-ink">{formatConsultationMoney(offer.amount, offer.currency)}</span>
                    </p>
                    <p className="mt-3 font-body text-xs text-copy/38 leading-relaxed">
                      {isLoadingPaymentConfig
                        ? 'Loading current payment settings...'
                        : paymentConfigError
                          ? 'Payment settings could not be loaded. We will follow up manually.'
                          : offer.ownerActionRequired
                            ? 'Payment setup needs owner review before checkout can start.'
                            : `Payment method: ${offer.providerLabel}.`}
                    </p>
                    {flowState.paymentProvider === 'swift' && flowState.bankTransferReference ? (
                      <div className="mt-4 space-y-2 border-t border-ink/10 pt-4">
                        <p className="font-body text-xs text-copy/58 leading-relaxed">
                          Transfer reference: <span className="text-accent-ink">{flowState.bankTransferReference}</span>
                        </p>
                        {swiftInstructions?.beneficiaryName ? (
                          <p className="font-body text-xs text-copy/38 leading-relaxed">Beneficiary: {swiftInstructions.beneficiaryName}</p>
                        ) : null}
                        {swiftInstructions?.bankName ? (
                          <p className="font-body text-xs text-copy/38 leading-relaxed">Bank: {swiftInstructions.bankName}</p>
                        ) : null}
                        {swiftInstructions?.iban ? (
                          <p className="font-body text-xs text-copy/38 leading-relaxed">IBAN: {swiftInstructions.iban}</p>
                        ) : null}
                        {swiftInstructions?.swiftBic ? (
                          <p className="font-body text-xs text-copy/38 leading-relaxed">SWIFT/BIC: {swiftInstructions.swiftBic}</p>
                        ) : null}
                      </div>
                    ) : null}
                    <FlowButton
                      type="button"
                      onClick={handleStartPayment}
                      disabled={!canStartPayment}
                      className="mt-5 w-full justify-center   px-5 py-3 text-[10px] uppercase tracking-[0.22em] font-body font-bold  transition-colors inline-flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid={`${sourcePage}-consultation-pay`}
                    >
                      {isCreatingOrder || isCapturingOrder ? (
                        <>
                          Processing <Loader2 className="w-4 h-4 animate-spin" />
                        </>
                      ) : (
                        <>
                          {offer.paymentProvider === 'swift' ? 'Get SWIFT Details' : 'Continue to Pay'}
                        </>
                      )}
                    </FlowButton>
                  </div>
                )
              ) : (
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-accent-ink mt-0.5 flex-shrink-0" />
                  <p className="font-body text-sm text-copy/70 leading-relaxed">No payment is required before booking.</p>
                </div>
              )}
            </div>

            <div className="group relative overflow-hidden border border-ink/10 bg-surface/85 p-5">
              <div className={`absolute inset-x-0 top-0 h-px ${canOpenCalendar ? 'bg-gold/70' : 'bg-ink/15'}`} />
              <div className="mb-4 flex items-center justify-between gap-4">
                <p className="text-[10px] uppercase tracking-[0.24em] text-copy/42 font-body">03 Calendar</p>
                <CalendarDays className={`h-5 w-5 ${canOpenCalendar ? 'text-accent-ink' : 'text-copy/38'}`} strokeWidth={1.5} />
              </div>
              <p className="font-body text-sm text-copy/58 leading-relaxed">
                {canOpenCalendar ? 'Payment is ready. Choose the slot that fits your team.' : 'Calendar unlocks after payment is confirmed.'}
              </p>
              <FlowButton
                type="button"
                onClick={handleOpenCalendar}
                disabled={!canOpenCalendar || !calendarUrl}
                className="mt-5 w-full justify-center border border-ink/20 bg-ink/[0.025] text-ink px-5 py-3 text-[10px] uppercase tracking-[0.22em] font-body hover:border-gold  transition-colors inline-flex items-center gap-3 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid={`${sourcePage}-consultation-calendar`}
              >
                Open Calendar <CalendarDays className="w-4 h-4" />
              </FlowButton>
              {flowState.bookingStatus === 'opened' && trustedCalendarUrl ? (
                <a
                  href={trustedCalendarUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-4 inline-flex items-center gap-2 text-accent-ink text-[10px] uppercase tracking-[0.2em] font-body"
                  data-testid={`${sourcePage}-consultation-calendar-link`}
                >
                  Direct booking link <ExternalLink className="w-3.5 h-3.5" />
                </a>
              ) : null}
            </div>
          </div>
        </div>

        <aside className="border-t border-ink/10 bg-black/20 p-6 md:p-8 xl:border-l xl:border-t-0">
          <p className="text-[10px] uppercase tracking-[0.28em] text-accent-ink/75 font-body mb-5">
            What happens next
          </p>
          <div className="space-y-5">
            {[
              'Magellan reviews the submitted information before recommending the next step.',
              `${getPaymentProviderLabel(offer.paymentProvider)} confirms the discovery call payment before calendar access.`,
              'A team member will follow up within 24 hours when manual review is needed.',
            ].map((item, index) => (
              <div key={item} className="flex gap-4">
                <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center border border-gold/25 text-[10px] text-accent-ink font-body">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <p className="font-body text-sm leading-relaxed text-copy/48">{item}</p>
              </div>
            ))}
          </div>
        </aside>
      </div>
    </section>
  );
}
